/** Tiny DOM helpers. Menus and the HUD are plain DOM: faster to build than
 * canvas UI, responsive for free, and readable by a screen reader — which also
 * happens to make the game inspectable in automated browser checks. */

type Attrs = Record<string, string | number | boolean | undefined>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'text') node.textContent = String(value);
    else node.setAttribute(key, value === true ? '' : String(value));
  }
  for (const child of children) {
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

export function clear(node: HTMLElement): void {
  while (node.firstChild) node.firstChild.remove();
}

/**
 * Inline SVG icons.
 *
 * Drawn rather than typed: the obvious characters for these ("⏸", "←") render
 * differently on every platform and some phones substitute an emoji, which is
 * the opposite of minimal.
 */
const ICON_PATHS: Record<string, string> = {
  back: '<path d="M13 4 6 10l7 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>',
  pause:
    '<rect x="6" y="5" width="3" height="10" rx="1.2" fill="currentColor"/><rect x="11" y="5" width="3" height="10" rx="1.2" fill="currentColor"/>',
  doors:
    '<path d="M10 4v12M6 7l-2 3 2 3M14 7l2 3-2 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  star: '<path d="M10 1.8l2.5 5.3 5.8.7-4.3 4 1.1 5.7L10 14.7l-5.1 2.8 1.1-5.7-4.3-4 5.8-.7z" fill="currentColor" stroke-linejoin="round"/>',
  forward:
    '<path d="M7 4l7 6-7 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>',
  // Teeth on a ring, so it reads as a cog. Rays around a circle read as a sun —
  // a light/dark switch — which is what the first settings icon was taken for.
  cog: '<circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="2.83 2.83"/><circle cx="10" cy="10" r="5" fill="none" stroke="currentColor" stroke-width="3"/>',
  // An open-ended spanner, head up and to the right: the garage.
  wrench:
    '<path transform="rotate(45 10 10)" fill="currentColor" d="M11.3 0.8A4.6 4.6 0 0 1 11.6 9.5V17A1.6 1.6 0 0 1 8.4 17V9.5A4.6 4.6 0 0 1 8.7 0.8V4.6H11.3Z"/>',
  home: '<path d="M3.5 9.6 10 4l6.5 5.6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/><path d="M5.6 8.4V16h3.2v-4h2.4v4h3.2V8.4" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>',
  retry:
    '<path d="M15.6 10.6a5.7 5.7 0 1 1-1.9-4.9" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><path d="M14.6 2.8v3.6h-3.6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>',
  play: '<path d="M6 3.6v12.8L16 10z" fill="currentColor" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>',
  // The settings sheet's rows.
  sound:
    '<path d="M3.5 7.5h3l4-3.5v12l-4-3.5h-3z" fill="currentColor" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M13.5 7.2a4 4 0 0 1 0 5.6M15.8 5a7 7 0 0 1 0 10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
  // The icon-first screens (PLAYTEST_LOG 2026-10-29): one fact, one icon.
  clock:
    '<circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" stroke-width="1.9"/><path d="M10 6v4.3l2.8 1.8" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>',
  person:
    '<circle cx="10" cy="5.6" r="3.2" fill="currentColor"/><path d="M4 17.5c.4-4 2.8-6.3 6-6.3s5.6 2.3 6 6.3z" fill="currentColor"/>',
  lost: '<circle cx="8" cy="5.6" r="3" fill="currentColor"/><path d="M2.5 17.5c.4-3.8 2.6-6 5.5-6s5.1 2.2 5.5 6z" fill="currentColor"/><path d="M14 4.5l4 4M18 4.5l-4 4" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>',
  trip: '<rect x="5.2" y="2.8" width="9.6" height="14.4" rx="2.2" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M10 5.8 7.8 8.4h4.4zM10 14.2l-2.2-2.6h4.4z" fill="currentColor"/>',
  rope: '<circle cx="10" cy="4.6" r="2.7" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M8.1 6.6v11M11.9 6.6v11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
  up: '<path d="M10 3.2l5.6 6.2h-3.6v6.6H8V9.4H4.4z" fill="currentColor"/>',
  // The music (ADR-0056): a pair of quavers.
  music:
    '<path d="M7.5 15.5V5.2l9-1.8v10.1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><ellipse cx="5.6" cy="15.5" rx="2.4" ry="1.9" fill="currentColor"/><ellipse cx="14.6" cy="13.5" rx="2.4" ry="1.9" fill="currentColor"/>',
  guide:
    '<path d="M8 11V4.2a1.4 1.4 0 0 1 2.8 0V9l3.6.8a1.8 1.8 0 0 1 1.4 2l-.6 3.6a2.4 2.4 0 0 1-2.4 2H9.6a2.4 2.4 0 0 1-2-1.1L5 12.5a1.3 1.3 0 0 1 2-1.6z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>',
  upload:
    '<path d="M10 13V3.5M6.2 7.2 10 3.4l3.8 3.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M3.5 12.5v3a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5v-3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  unlock:
    '<rect x="4" y="9" width="12" height="8.5" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><path d="M7 9V6.5a3 3 0 0 1 5.8-1.1" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="10" cy="13" r="1.4" fill="currentColor"/>',
  coin: '<circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" stroke-width="2"/><path d="M10 6.5v7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  // Power-ups (ADR-0055): the charge, and the four.
  power:
    '<path d="M11.5 2 4.5 11.2h4.6L8 18l7.5-9.4h-4.7z" fill="currentColor" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>',
  squeeze:
    '<path d="M2.5 10h5M5.2 7.3 7.9 10l-2.7 2.7M17.5 10h-5M14.8 7.3 12.1 10l2.7 2.7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 4v12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  hold: '<circle cx="10" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M8.2 8v5M11.8 8v5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><path d="M8 2.5h4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  announce:
    '<path d="M3 8.2v3.6h3l5.5 3.7V4.5L6 8.2z" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"/><path d="M14 7.5a3.5 3.5 0 0 1 0 5" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/><path d="M6.5 12v3.5" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>',
  stairs:
    '<path d="M3 16.5h4v-4h4v-4h4v-4h2.5" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/>',
  // Stops in order (ADR-0052): a short list.
  queue:
    '<circle cx="5" cy="5" r="1.8" fill="currentColor"/><circle cx="5" cy="10" r="1.8" fill="currentColor"/><circle cx="5" cy="15" r="1.8" fill="currentColor"/><path d="M9 5h7M9 10h7M9 15h7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  close:
    '<path d="M5 5l10 10M15 5 5 15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>',
  chevron:
    '<path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>',
};

/** A coin, in its own gold: the purse on the home screen and in the garage. */
export function coinIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML =
    '<circle cx="10" cy="10" r="8" fill="#e6c98a" stroke="#875a06" stroke-width="2"/><path d="M10 6v8" stroke="#875a06" stroke-width="2" stroke-linecap="round"/>';
  return svg;
}

/** A brass call button with one icon: the HUD's, the home screen's, the results'. */
export function callButton(name: string, label: string, onClick: () => void): HTMLButtonElement {
  const button = el('button', { class: 'call-button', type: 'button', 'aria-label': label }, [
    icon(name),
  ]);
  button.addEventListener('click', onClick);
  return button;
}

export function icon(name: keyof typeof ICON_PATHS | string): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.innerHTML = ICON_PATHS[name] ?? '';
  return svg;
}

export function starString(stars: number): string {
  return '★'.repeat(stars) + '☆'.repeat(Math.max(0, 3 - stars));
}
